import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Header from '../components/Header';
import OperationalMap from '../components/OperationalMap';

const parseArray = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    if (typeof field === 'string') {
        const trimmed = field.trim();
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
            return trimmed.slice(1, -1).split(',').map(s => s.trim().replace(/^"|"$/g, '')).filter(s => s !== '');
        }
        try { const p = JSON.parse(trimmed); if (Array.isArray(p)) return p; } catch {}
        return trimmed.split(',').map(s => s.trim()).filter(s => s !== '');
    }
    return [field];
};

const MapaPage = () => {
    const navigate = useNavigate();

    const [municipioInfo, setMunicipioInfo] = useState({ id: null, nome: '', total_imoveis: 0, meta_visitas_anuais: 0 });
    const [mapRegistros, setMapRegistros] = useState([]);
    const [mapExames, setMapExames]       = useState([]);
    const [pitsLista, setPitsLista]       = useState([]);
    const [allAgents, setAllAgents]       = useState([]);
    const [isLoading, setIsLoading]       = useState(true);
    const [error, setError]               = useState(null);

    useEffect(() => {
        const municipioId = localStorage.getItem('user_municipio_id');
        if (!municipioId) { navigate('/'); return; }

        const fetchAll = async () => {
            try {
                // Busca paralela dos dados de suporte
                const [municipioRes, agsRes, pitsRes, regsRes] = await Promise.all([
                    supabase.from('municipios').select('id, nome, total_imoveis, meta_visitas_anuais').eq('id', municipioId).single(),
                    supabase.from('agentes').select('id, nome').eq('municipio_id', municipioId).order('nome'),
                    supabase.from('denuncias').select('*').eq('cidade', municipioId),
                    supabase.from('ocorrencias_view').select('*').eq('municipio_id', municipioId).order('created_at', { ascending: false }),
                ]);

                const registros = (regsRes.data || []).map(item => ({
                    ...item,
                    data: item.data_atividade,
                    localidade: item.nome_localidade_view || item.nome_localidade || item.endereco || 'Não Informada',
                    agente_nome: item.nome_agente_view || '',
                    tipo_atividade: parseArray(item.tipo_atividade),
                    barbeiros_intradomicilio: Number(item.barbeiros_intradomicilio) || 0,
                    barbeiros_peridomicilio: Number(item.barbeiros_peridomicilio) || 0,
                    vestigios_intradomicilio: item.vestigios_intradomicilio,
                    vestigios_peridomicilio: item.vestigios_peridomicilio,
                }));

                // Busca exames após ter os IDs das ocorrências
                let examesData = [];
                const ocorrenciaIds = registros.map(r => r.id).filter(Boolean);
                if (ocorrenciaIds.length > 0) {
                    const examesRes = await supabase
                        .from('exames')
                        .select('*, exames_exemplares(*)')
                        .in('ocorrencia_id', ocorrenciaIds);
                    examesData = examesRes.data || [];
                }

                // Todos os setStates no mesmo bloco síncrono → React 18 batches em 1 render
                setMunicipioInfo(municipioRes.data || { id: null, nome: '', total_imoveis: 0, meta_visitas_anuais: 0 });
                setAllAgents(agsRes.data || []);
                setPitsLista(pitsRes.data || []);
                setMapRegistros(registros);
                setMapExames(examesData);
                setIsLoading(false);
            } catch (err) {
                console.error('Erro ao carregar dados do mapa:', err);
                setError('Não foi possível carregar os dados. Verifique sua conexão.');
                setIsLoading(false);
            }
        };

        fetchAll();
    }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

    const allLocalitiesForMap = useMemo(
        () => pitsLista.map(p => ({ id: p.localidade_id, nome: p.localidade_nome })),
        [pitsLista]
    );

    return (
        <div className="portal-layout">
            <Header activeView="mapa" />
            <main className="main-content">
                {isLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, color: '#64748b' }}>
                        <div className="spinner" />
                        <p style={{ margin: 0, fontSize: 14 }}>Carregando dados do mapa...</p>
                    </div>
                ) : error ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#ef4444', padding: 24 }}>
                        <p>{error}</p>
                    </div>
                ) : (
                    <OperationalMap
                        registros={mapRegistros}
                        pits={pitsLista}
                        exames={mapExames}
                        municipioInfo={municipioInfo}
                        allAgents={allAgents}
                        allLocalities={allLocalitiesForMap}
                    />
                )}
            </main>
        </div>
    );
};

export default MapaPage;
